import ProductFilter from '../../../../common/components/ProductFilter.js'
import {
  createInventoryTransfer,
  closeCreateInventoryTransferModal,
  updateCreateInventoryTransferModalForm,
  NO_WAREHOUSE_SELECTED,
  createInventoryTransferModalFormSelector,
  hasErrorsSelector,
  warehouseSelectionIsValidSelector,
} from './createInventoryTransferModalFunctions.js'
import {nonFBAWarehouseOptionsSelector} from '../../../../data/warehouses.js'
import ConfirmModal from '../../../../common/components/Modal/ConfirmModal.js'
import ProductRow from './ProductRow.js'
import {onlyIfForm, useSelector} from '../../../../store.js'

function CreateInventoryTransferModal({form}) {
  const hasErrors = useSelector(hasErrorsSelector)
  const warehouseOptions = useSelector(nonFBAWarehouseOptionsSelector)
  const warehouseSelectionIsValid = useSelector(
    warehouseSelectionIsValidSelector,
  )

  return (
    <ConfirmModal
      title={`Transfer Inventory`}
      modalSize="x-lg"
      isSaving={form.isSaving}
      isDisabled={hasErrors}
      confirmText="Transfer"
      cancelText="Cancel"
      onConfirm={() => createInventoryTransfer()}
      onCancel={() => closeCreateInventoryTransferModal()}
      error={form.serverError}
    >
      <>
        <p className="fs-01 margin-bottom-20">
          <strong>
            Select the warehouses that will be involved in the transfer
          </strong>
        </p>
        <div className="flex margin-bottom-25">
          <div>
            <label className="fs-00" htmlFor="id_from_warehouse">
              Sending
            </label>
            <div className="flex">
              <select
                className="select select--tall"
                name="from_warehouse"
                id="id_from_warehouse"
                value={form.sending_warehouse_id}
                onChange={(event) => {
                  const sending_warehouse_id = Number(event.target.value)

                  if (sending_warehouse_id === form.receiving_warehouse_id) {
                    updateCreateInventoryTransferModalForm({
                      receiving_warehouse_id: form.sending_warehouse_id,
                      sending_warehouse_id,
                    })
                  } else {
                    updateCreateInventoryTransferModalForm({
                      sending_warehouse_id,
                    })
                  }
                }}
              >
                <option
                  className="placeholder"
                  disabled
                  value={NO_WAREHOUSE_SELECTED}
                >
                  Select sending warehouse...
                </option>
                {warehouseOptions.map(({value, display}) => (
                  <option key={value} value={value}>
                    {display}
                  </option>
                ))}
              </select>
              <div className="flex--justify-col">
                <strong className="fs-04 lh-sm op-50 margin-left-10 margin-right-10">
                  ⤏
                </strong>
              </div>
            </div>
          </div>
          <div className="margin-right-20">
            <label className="fs-00" htmlFor="id_to_warehouse">
              Receiving
            </label>
            <select
              className="select select--tall"
              name="to_warehouse"
              id="id_to_warehouse"
              value={form.receiving_warehouse_id}
              onChange={(event) => {
                const receiving_warehouse_id = Number(event.target.value)

                if (receiving_warehouse_id === form.sending_warehouse_id) {
                  updateCreateInventoryTransferModalForm({
                    sending_warehouse_id: form.receiving_warehouse_id,
                    receiving_warehouse_id,
                  })
                } else {
                  updateCreateInventoryTransferModalForm({
                    receiving_warehouse_id,
                  })
                }
              }}
            >
              <option
                className="placeholder"
                disabled
                value={NO_WAREHOUSE_SELECTED}
              >
                Select receiving warehouse...
              </option>
              {warehouseOptions.map(({value, display}) => (
                <option key={value} value={value}>
                  {display}
                </option>
              ))}
            </select>
          </div>
          {warehouseSelectionIsValid && (
            <div className="w-40 divider--left margin-left-0 padding-left-20">
              <label className="fs-00" htmlFor="id_to_warehouse">
                Transfer Notes
              </label>
              <textarea
                className="textarea margin-bottom-10"
                value={form.transfer_notes}
                onChange={(event) =>
                  updateCreateInventoryTransferModalForm({
                    transfer_notes: event.target.value,
                  })
                }
              />
            </div>
          )}
        </div>
      </>
      {warehouseSelectionIsValid && (
        <>
          <hr />

          <div>
            <p className="fs-01 margin-bottom-20">
              <strong>Select the products to be transferred</strong>
            </p>
            <table className="table margin-top-10 margin-bottom-25">
              <thead>
                <tr>
                  <th
                    rowSpan="2"
                    className="table__th table__th--sm th-width-15"
                  >
                    Product/SKU
                  </th>
                  <th
                    colSpan="2"
                    className="table__th table__th--top-border table__th--sm border-left--dark align-center"
                  >
                    Sending Warehouse
                  </th>
                  <th
                    rowSpan="2"
                    className="table__th table__th--sm border-left--dark align-center"
                  >
                    Transfer Quantity
                  </th>
                  <th
                    colSpan="2"
                    className="table__th table__th--top-border table__th--sm border-left--dark align-center"
                  >
                    Receiving Warehouse
                  </th>
                  <th
                    rowSpan="2"
                    className="table__th table__th--sm border-left--dark"
                  >
                    Line Notes
                  </th>
                  <th rowSpan="2" className="table__th table__th--sm" />
                </tr>
                <tr>
                  <th className="table__th table__th--sm border-left--dark align-center">
                    Committed
                  </th>
                  <th className="table__th table__th--sm border-left--dark align-center">
                    Physical On Hand
                  </th>
                  <th className="table__th table__th--sm border-left--dark align-center">
                    Committed
                  </th>
                  <th className="table__th table__th--sm border-left--dark align-center">
                    Physical On Hand
                  </th>
                </tr>
              </thead>
              <tbody className="table__tbody--lines">
                {form.skus.map((sku, index) => (
                  <ProductRow
                    key={sku}
                    sku={sku}
                    autoFocus={
                      form.afterAddingProduct
                        ? form.skus.length - 1 === index
                        : index === 0
                    }
                  />
                ))}
                <tr>
                  <td className="table__td padding-left-0" colSpan="2">
                    <ProductFilter
                      label="Add a product to be transferred"
                      dropdown="inventory_transfer_product_filter"
                      onSelect={({sku}) => {
                        const willAdd = !form.skus.includes(sku)

                        updateCreateInventoryTransferModalForm({
                          skus: willAdd ? [...form.skus, sku] : form.skus,
                          afterAddingProduct: willAdd,
                        })
                      }}
                    />
                  </td>
                  <td className="table__td" colSpan="8" />
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(
  CreateInventoryTransferModal,
  createInventoryTransferModalFormSelector,
)
